import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '~/components/language-switcher';
import { useLogout } from '~/hooks/use-auth';
import { LockedBadge } from './locked-badge';
import { useParticipateCta } from './participate-cta';
import type { NotEligibleReason } from './not-eligible';

interface HeaderProps {
  snowOn: boolean;
  onToggleSnow: () => void;
  /** null — баланс не показываем (участие не подтверждено). */
  balance: number | null;
  participationConfirmed: boolean;
  /** Не участвует в акции — кнопки «Участвую в акции» нет. */
  notEligible: NotEligibleReason | null;
  scrolled: boolean;
  participant: { participantId: string; fullName: string };
}

/** Шапка лендинга (`header#hdr` макета): меню, переключатель снега, CTA, баланс козочек, участник. */
export function LandingHeader({ snowOn, onToggleSnow, balance, participationConfirmed, notEligible, scrolled, participant }: HeaderProps) {
  const { t } = useTranslation('landing');
  const cta = useParticipateCta();

  return (
    <header id="hdr" className={scrolled ? 'scrolled' : undefined}>
      <div className="wrap hbar">
        <a className="logo" href="#top">
          <i className="ico i-nl" />
          <span>{t('header.logoSuffix')}</span>
        </a>
        <nav className="hnav">
          <a href="#prizes">{t('header.nav.prizes')}</a>
          <a href="#how">{t('header.nav.how')}</a>
          <a href="#gifts">{t('header.nav.gifts')}</a>
          <a href="#faq">{t('header.nav.faq')}</a>
          <Link to="/rules" data-rules="">
            {t('header.nav.rules')}
          </Link>
        </nav>
        <div className="hright">
          <LanguageSwitcher />
          <button
            type="button"
            className={snowOn ? 'snowtoggle' : 'snowtoggle off'}
            title={t('header.snowToggleAria')}
            aria-label={t('header.snowToggleAria')}
            onClick={onToggleSnow}
          >
            <i className="ico i-snow" />
          </button>
          {!participationConfirmed && !notEligible && (
            <a className="btn hcta" href={cta.href} onClick={cta.onClick} aria-busy={cta.pending}>
              <i className="ico i-goat" />
              <span className="full">{t('header.ctaFull')}</span>
              <span className="short">{t('header.ctaShort')}</span>
            </a>
          )}
          {participationConfirmed && balance !== null ? (
            <a className="chip" href="#play" aria-label={t('header.balanceAria')}>
              <i className="ico i-goat" />
              <span className="bal">{balance}</span>
            </a>
          ) : (
            <span className="chip">
              <i className="ico i-goat" />
              <LockedBadge />
            </span>
          )}
          <ParticipantMenu participantId={participant.participantId} fullName={participant.fullName} />
        </div>
      </div>
    </header>
  );
}

/** ФИО и ID вошедшего (landing, «Данные вошедшего участника в шапке»); «Выйти» — в меню по нажатию.
 *  На узком экране вместо ФИО и ID — пиктограмма, они видны в меню. */
function ParticipantMenu({ participantId, fullName }: { participantId: string; fullName: string }) {
  const { t } = useTranslation('landing');
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const name = fullName.trim();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="who" ref={ref}>
      <button type="button" className="whobtn" aria-haspopup="menu" aria-expanded={open} aria-label={t('header.participantAria')} title={name ? `${name} · ${participantId}` : participantId} onClick={() => setOpen((v) => !v)}>
        <span className="whoava" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="8.5" r="3.8" />
            <path d="M4.5 20c1.3-3.6 4.2-5.4 7.5-5.4s6.2 1.8 7.5 5.4" />
          </svg>
        </span>
        <span className="whotx">
          {name ? <b>{name}</b> : <b>{participantId}</b>}
          {name && <small>{participantId}</small>}
        </span>
      </button>
      {open && (
        <div className="whomenu" role="menu">
          {name && <b>{name}</b>}
          <small>{t('header.participantId', { id: participantId })}</small>
          <button type="button" role="menuitem" onClick={() => logout.mutate()} disabled={logout.isPending}>
            {t('header.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
